<template>
  <div class="p-question__body__judge">
    <div class="judgeTitle">
      <span v-if="fromJudgePage" class="tag">解答例</span>
      <span v-else class="tag">{{showingAnswerLabel}}</span>
    </div>
    <!-- 順不同記述式解答欄の場合、注釈を表示 -->
    <div v-for="(answer, index) in answerExplanations">
      <span v-if="isRandomAnswerLabel(answer)">{{ displayRandomAnswerLabel(answer.label) }}</span>
    </div>
    <ol class="answerList answerList--answered">
      <template v-for="(answer, index) in answerExplanations" :key="'grading_'+index">
        <!-- 順不同記述式解答欄の場合 -->
        <template v-if="isRandomAnswerLabel(answer)">
          <li  v-for="(item, idx) in answer.answers"
            :class="{ 'size size--half': answer.isHalf, 'math-field': isKeyboard }"
            >
              <div v-if="isKeyboard && answer.label && answer.label[idx]" class="number" v-html="labelText(answer.label[idx])"></div>
              <span v-else-if="answer.label && answer.label[idx]" class="number">{{ answer.label[idx] }}</span>
              <div v-if="answer.suffix || answer.prefix" :class="{ 'answerList__inner': true, 'answerList__inner--ScoringCriteria': answer.gradingHint }">
                <div :class="{ 'stringBox': true, 'stringBox--example': true, 'stringBox--lines': stringBoxLines(answer.isMulti) }">
                  <div class="stringBox__string" v-if="isKeyboard && answer.prefix.length > 0" v-html="prefixText(answer)"></div>
                  <div class="stringBox__string" v-else-if="answer.prefix.length > 0">{{ answer.prefix }}</div>
                  <div :class="{ 'answerTextarea': true, 'answerTextarea--judge': true, 'debug-markdown': isDebug }">
                    <markdown-view :rawText="item" :souruby="isSouruby" :workId="workId"
                                  class="answerText"></markdown-view>
                  </div>
                  <div class="stringBox__string" v-if="isKeyboard && answer.suffix.length > 0" v-html="suffixText(answer)"></div>
                  <div class="stringBox__string" v-else-if="answer.suffix.length > 0">{{ answer.suffix }}</div>
                </div>
                <markdown-view class="ScoringCriteria" :rawText="answer.gradingHint" :workId="workId"
                                :souruby="isSouruby" v-if="answer.gradingHint"></markdown-view>
              </div>
              <div v-else :class="{ 'answerTextarea': true, 'answerTextarea--judge': true, 'debug-markdown': isDebug }">
                <markdown-view :rawText="item" :souruby="isSouruby" :workId="workId"
                              class="answerText"></markdown-view>
                <markdown-view class="ScoringCriteria" :rawText="answer.gradingHint" :workId="workId"
                            :souruby="isSouruby" v-if="answer.gradingHint"></markdown-view>
              </div>
          </li>
        </template>
        <!-- 順不同記述式解答欄以外の場合 -->
        <template v-else>
          <li :class="{ 'size size--half': answer.isHalf, 'math-field': isKeyboard }">
            <div v-if="isKeyboard && answer.label.length > 0" class="number" v-html="labelText(answer.label)"></div>
            <span v-else-if="answer.label" class="number">{{ answer.label }}</span>
            <div v-if="answer.suffix || answer.prefix" :class="{ 'answerList__inner': true, 'answerList__inner--ScoringCriteria': answer.gradingHint }">
              <div :class="{ 'stringBox': true, 'stringBox--example': true, 'stringBox--lines': stringBoxLines(answer.isMulti) }">
                <div class="stringBox__string" v-if="isKeyboard && answer.prefix.length > 0" v-html="prefixText(answer)"></div>
                <div class="stringBox__string" v-else-if="answer.prefix.length > 0">{{ answer.prefix }}</div>
                <div :class="{ 'answerTextarea': true, 'answerTextarea--judge': true, 'debug-markdown': isDebug }">
                  <markdown-view :rawText="answer.answers.join(' ／ ')" :souruby="isSouruby" :workId="workId"
                                class="answerText"></markdown-view>
                </div>
                <div class="stringBox__string" v-if="isKeyboard && answer.suffix.length > 0" v-html="suffixText(answer)"></div>
                <div class="stringBox__string" v-else-if="answer.suffix.length > 0">{{ answer.suffix }}</div>
              </div>
              <markdown-view class="ScoringCriteria" :rawText="answer.gradingHint" :workId="workId"
                              :souruby="isSouruby" v-if="answer.gradingHint"></markdown-view>
            </div>
            <div v-else :class="{ 'answerTextarea': true, 'answerTextarea--judge': true, 'debug-markdown': isDebug }">
              <markdown-view :rawText="answer.answers.join(' ／ ')" :souruby="isSouruby" :workId="workId"
                            class="answerText"></markdown-view>
              <markdown-view class="ScoringCriteria" :rawText="answer.gradingHint" :workId="workId"
                          :souruby="isSouruby" v-if="answer.gradingHint"></markdown-view>
            </div>
          </li>
        </template>
      </template> 
    </ol>
    <!-- 解説を表示 -->
    <div v-if="commentary">
      <div class="judgeTitle"><span class="tag">解説</span></div>
      <div :class="{'judgeTextCont':true, 'debug-markdown': isDebug}">
        <slot name="commentaryMarkdown">
          <markdown-view :rawText="commentary" :workId="workId" :souruby="isSouruby" v-if="workId!==0"></markdown-view>
          <markdown-view :rawText="commentary" :souruby="isSouruby" v-else></markdown-view>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {ref, computed} from "vue"
  import { markdown as md } from "@/util/markdown";
  import {QUIZTYPE} from '@/consts/answer_column';

  interface IAnswer{
    answers: Array<any>;
    label: string | Array<string>;
    isHalf: boolean;
    isMulti: boolean;
    gradingHint?: string;
    prefix?: string;
    suffix?: string;
    isRandomAnswer?: number;
  }

  const answerLabel = ref('')
  
  const props = withDefaults(
    defineProps<{
      autoGradingPage?: boolean;
      isVertical?: boolean;
      answerExplanations: Array<IAnswer>;
      commentary?: string;
      workId?: number;
      fromJudgePage?: boolean;
      isSouruby?: boolean;
      isDebug?: boolean;
      isKeyboard?: boolean;
    }>(),
    {
      autoGradingPage: false,
      isVertical: false,
      commentary: "",
      workId: 0,
      fromJudgePage: false,
      isSouruby: false,
      isDebug: false,
      isKeyboard: false,
    }
  );

  const showingAnswerLabel = computed(() => {
    if(props.autoGradingPage){
      return answerLabel.value = "解答"
    }else{
      return answerLabel.value = "解答例"
    }
  })

  function stringBoxLines(isMulti){
    if(props.isVertical){
      return true
    }else{
      if(isMulti){
        return true
      }
    }
    return false
  }

  function labelText(text) {
    return md.render(text); 
  }

  function prefixText(answer){
    return md.render(answer.prefix);
  }

  function suffixText(answer){
    return md.render(answer.suffix);
  }

  // NFR-4778 順不同記述式かどうかを判定する。
  function isRandomAnswerLabel(answer){
    return answer.isRandomAnswer === QUIZTYPE.WRITTEN_RANDOM_ORDER
  }
  // NFR-4778 順不同記述式ラベルを表示する。
  function displayRandomAnswerLabel(label){
    if (Array.isArray(label)){
      // ラベルがすべて空でない場合のみ、順不同である旨のメッセージを表示
      if(label.length > 0 && label.every((value) => value.trim() !== "")){
        return `※${label.join(' ')}は順不同。`
      }else{ // ラベルを設定しない場合は非表示にする。（運用上必須入力にする）
        return ""
      }
    }
  }
</script>